import React from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { bodyCss } from '../text'
import { TabItemProps } from './index'

const tabBorderWidth = 1
const borderRadius = 3
const borderColor = theme.color.neutral40

const getBorderRadius = (
  topLeft: number,
  topRight: number,
  bottomRight: number,
  bottomLeft: number
): string => {
  return (
    topLeft + 'px ' + topRight + 'px ' + bottomRight + 'px ' + bottomLeft + 'px'
  )
}

const genericTabCss = (): string =>
  cx(
    css`
      display: flex;
      padding: 10px 20px;
      text-decoration: none;
      user-select: none;
      border-style: solid;

      justify-content: center;
      cursor: pointer;
      border-color: ${borderColor};

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      word-wrap: normal;
    `,
    bodyCss
  )

const unfocusedTabCss = css`
  border-width: 0px 0px ${tabBorderWidth}px 0px;
  cursor: pointer;

  &:hover {
    outline: 0;
    text-decoration: initial;
    background-color: ${theme.color.neutral30};
  }
`

const focusedTabCss = css`
  cursor: default;
  border-width: ${tabBorderWidth}px ${tabBorderWidth}px 0px ${tabBorderWidth}px;
  border-radius: ${getBorderRadius(borderRadius, borderRadius, 0, 0)};
`

interface TabProps {
  readonly label: string
  readonly isFocused: boolean
  readonly onClick: () => void
}

function Tab({ label, isFocused, onClick }: TabProps) {
  return (
    <div
      className={cx(
        genericTabCss(),
        isFocused ? focusedTabCss : unfocusedTabCss
      )}
      onClick={onClick}
    >
      {label}
    </div>
  )
}

interface ButtonGroupToggleProps<TItemID> {
  readonly items: Array<TabItemProps<TItemID>>
  readonly selectedItem: TabItemProps<TItemID>
  readonly onSelect: (item: TabItemProps<TItemID>) => void
}

export function TabGroupToggle<TItemID extends string>({
  items,
  selectedItem,
  onSelect
}: ButtonGroupToggleProps<TItemID>) {
  if (
    !items ||
    items.length === 0 ||
    !items.some((i) => i.id === selectedItem.id)
  ) {
    throw new Error('valid items and selectedItem are required')
  }

  return (
    <div
      className={css`
        display: flex;
        flex-direction: row;
      `}
    >
      {items.map((item, index) => (
        <Tab
          key={index}
          label={item.label}
          isFocused={!!selectedItem ? selectedItem.id === item.id : false}
          onClick={() => onSelect(item)}
        />
      ))}
      <span
        className={css`
          display: flex;
          flex: 1;
          border-style: solid;
          border-width: 0px 0px ${tabBorderWidth}px 0px;
          border-color: ${borderColor};
        `}
      />
    </div>
  )
}
